/**
 * Centralized logging utility for Inzif Widget SDK
 * Provides secure, conditional logging that respects debug configuration
 */

export interface LoggerConfig {
  debug: boolean;
  prefix?: string;
}

export class Logger {
  private static instance: Logger;
  private config: LoggerConfig;

  private constructor(config: LoggerConfig) {
    this.config = config;
  }

  /**
   * Initialize the logger with configuration
   */
  public static init(config: LoggerConfig): void {
    Logger.instance = new Logger(config);
  }

  /**
   * Get the logger instance
   */
  public static getInstance(): Logger {
    if (!Logger.instance) {
      // Fallback to disabled logging if not initialized
      Logger.instance = new Logger({ debug: false });
    }
    return Logger.instance;
  }

  /**
   * Update logger configuration
   */
  public static updateConfig(config: Partial<LoggerConfig>): void {
    if (Logger.instance) {
      Logger.instance.config = { ...Logger.instance.config, ...config };
    }
  }

  /**
   * Log debug information (only when debug is enabled)
   */
  public static debug(_message: string, ..._args: any[]): void {
    // Disabled by default - only enable for critical debugging
    // const logger = Logger.getInstance();
    // if (logger.config.debug) {
    //   const prefix = logger.config.prefix ? `[${logger.config.prefix}] ` : '';
    //   console.log(`${prefix}DEBUG: ${message}`, ...args);
    // }
  }

  /**
   * Log info information (only when debug is enabled)
   */
  public static info(_message: string, ..._args: any[]): void {
    // Disabled by default - only enable for critical debugging
    // const logger = Logger.getInstance();
    // if (logger.config.debug) {
    //   const prefix = logger.config.prefix ? `[${logger.config.prefix}] ` : '';
    //   console.log(`${prefix}INFO: ${message}`, ...args);
    // }
  }

  /**
   * Log warnings (always shown, but can be controlled)
   */
  public static warn(message: string, ...args: any[]): void {
    const logger = Logger.getInstance();
    if (logger.config.debug) {
      const prefix = logger.config.prefix ? `[${logger.config.prefix}] ` : '';
      console.warn(`${prefix}WARN: ${message}`, ...args);
    }
  }

  /**
   * Log errors (always shown, but sanitized)
   */
  public static error(message: string, error?: any): void {
    const logger = Logger.getInstance();
    const prefix = logger.config.prefix ? `[${logger.config.prefix}] ` : '';
    
    if (logger.config.debug) {
      console.error(`${prefix}ERROR: ${message}`, error);
    } else {
      // In production, only log sanitized error messages
      console.error(`${prefix}ERROR: ${message}`);
    }
  }

  /**
   * Sanitize sensitive data from objects before logging
   */
  public static sanitize(data: any): any {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sensitiveKeys = [
      'password', 'token', 'key', 'secret', 'credential', 'auth',
      'access_token', 'refresh_token', 'apiKey', 'clientId', 'clientSecret'
    ];

    const sanitized = Array.isArray(data) ? [] : {};

    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      const isSensitive = sensitiveKeys.some(sensitiveKey => 
        lowerKey.includes(sensitiveKey)
      );

      if (isSensitive) {
        if (typeof value === 'string' && value.length > 4) {
          (sanitized as any)[key] = '****' + value.slice(-4);
        } else {
          (sanitized as any)[key] = '****';
        }
      } else if (typeof value === 'object' && value !== null) {
        (sanitized as any)[key] = Logger.sanitize(value);
      } else {
        (sanitized as any)[key] = value;
      }
    }

    return sanitized;
  }

  /**
   * Log with automatic sanitization
   */
  public static debugSafe(message: string, data?: any): void {
    const logger = Logger.getInstance();
    if (logger.config.debug) {
      const sanitizedData = data ? Logger.sanitize(data) : undefined;
      Logger.debug(message, sanitizedData);
    }
  }

  /**
   * Log API responses safely (sanitizes sensitive data)
   */
  public static logApiResponse(message: string, response: any): void {
    const logger = Logger.getInstance();
    if (logger.config.debug) {
      const sanitizedResponse = Logger.sanitize(response);
      Logger.debug(message, sanitizedResponse);
    }
  }

  /**
   * Log authentication events safely
   */
  public static logAuth(message: string, token?: any): void {
    const logger = Logger.getInstance();
    if (logger.config.debug) {
      if (token) {
        const sanitizedToken = {
          ...token,
          access_token: token.access_token ? '****' + token.access_token.slice(-4) : undefined,
          expires_at: token.expires_at
        };
        Logger.debug(message, sanitizedToken);
      } else {
        Logger.debug(message);
      }
    }
  }
}

/**
 * Convenience functions for direct use
 */
export const log = {
  debug: Logger.debug,
  info: Logger.info,
  warn: Logger.warn,
  error: Logger.error,
  debugSafe: Logger.debugSafe,
  logApiResponse: Logger.logApiResponse,
  logAuth: Logger.logAuth,
  sanitize: Logger.sanitize
};
